/**
 * Footer component
 *
 * Displays avenues to contact you.
 * Contact information is passed in from the App component that
 * renders the Footer.
 *
 * If a social value has an empty string it will not be displayed.
 */
import React from "react";
import PropTypes from "prop-types";

import devDotToIcon from "url:../images/socials/devdotto.svg";
import envelopeIcon from "url:../images/socials/envelope.svg";
import gitHubIcon from "url:../images/socials/github.svg";
import instagramIcon from "url:../images/socials/instagram.svg";
import linkedInIcon from "url:../images/socials/linkedin.svg";
import mediumIcon from "url:../images/socials/medium.svg";
import twitterIcon from "url:../images/socials/twitter.svg";
import youTubeIcon from "url:../images/socials/youtube.svg";

const normalizeUrl = (value) => {
  if (!value) {
    return "";
  }

  return /^https?:\/\//i.test(value) ? value : `https://${value}`;
};

const resolveGitHubUrl = (value) => {
  if (!value) {
    return "";
  }

  if (value.includes("github.com")) {
    return normalizeUrl(value);
  }

  return `https://github.com/${value}`;
};

const resolveLinkedInUrl = (value) => {
  if (!value) {
    return "";
  }

  if (value.includes("linkedin.com")) {
    return normalizeUrl(value);
  }

  return `https://www.linkedin.com/in/${value}`;
};

/**
 * 💡 Learning resources
 *
 *  HTML hyperlinks: https://www.w3schools.com/html/html_links.asp
 *  Opening links in new tabs: https://www.freecodecamp.org/news/how-to-use-html-to-open-link-in-new-tab/
 */

const Footer = ({
  devDotTo,
  email,
  gitHub,
  instagram,
  linkedIn,
  medium,
  name = "",
  primaryColor,
  twitter,
  youTube,
}) => {

  const gitHubUrl = resolveGitHubUrl(gitHub);
  const linkedInUrl = resolveLinkedInUrl(linkedIn);

  return (
    <footer id="footer" className="site-footer" style={{ backgroundColor: primaryColor }}>
      <div className="footer-intro">
        <h2>Let&apos;s Build Something Useful</h2>
        <p>
          I am open to roles and collaborations where software engineering and AI can create real
          product impact.
        </p>
      </div>
      <div className="social-row">
        {email && (
          <a href={`mailto:${email}`} aria-label="Send email">
            <img src={envelopeIcon} alt="email" className="socialIcon" />
          </a>
        )}
        {devDotTo && (
          <a
            href={`https://dev.to/${devDotTo}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Visit Dev.to profile"
          >
            <img src={devDotToIcon} alt="Dev.to" className="socialIcon" />
          </a>
        )}
        {gitHubUrl && (
          <a
            href={gitHubUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Visit GitHub profile"
          >
            <img src={gitHubIcon} alt="GitHub" className="socialIcon" />
          </a>
        )}
        {instagram && (
          <a
            href={`https://www.instagram.com/${instagram}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Visit Instagram profile"
          >
            <img src={instagramIcon} alt="Instagram" className="socialIcon" />
          </a>
        )}
        {linkedInUrl && (
          <a
            href={linkedInUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Visit LinkedIn profile"
          >
            <img src={linkedInIcon} alt="LinkedIn" className="socialIcon" />
          </a>
        )}
        {medium && (
          <a
            href={`https://medium.com/@${medium}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Visit Medium profile"
          >
            <img src={mediumIcon} alt="Medium" className="socialIcon" />
          </a>
        )}
        {twitter && (
          <a
            href={`https://twitter.com/${twitter}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Visit X profile"
          >
            <img src={twitterIcon} alt="Twitter" className="socialIcon" />
          </a>
        )}
        {youTube && (
          <a
            href={`https://www.youtube.com/c/${youTube}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Visit YouTube channel"
          >
            <img src={youTubeIcon} alt="YouTube" className="socialIcon" />
          </a>
        )}
      </div>
      <p className="small footer-credit">
        Created by {name}
      </p>
    </footer>
  );
};

Footer.propTypes = {
  devDotTo: PropTypes.string,
  email: PropTypes.string,
  gitHub: PropTypes.string,
  instagram: PropTypes.string,
  linkedIn: PropTypes.string,
  medium: PropTypes.string,
  name: PropTypes.string.isRequired,
  primaryColor: PropTypes.string,
  twitter: PropTypes.string,
  youTube: PropTypes.string,

};

export default Footer;
